import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { usePermissionCheck } from '@/hooks/usePermissionCheck';
import {
  Plus,
  Edit,
  Trash2,
  Search,
  Users,
  Mail,
  Shield,
  EyeOff,
  Filter,
  MoreHorizontal,
  UserCheck,
  Calendar,
  CheckCircle,
  Settings,
  Car,
  Clock,
  X,
  Key,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { User, Role, Permission } from "@/types/auth";
import {
  useConfirmDialog,
  createDeleteConfirm,
  createDeactivateConfirm,
} from "@/components/ui/confirm-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import DataTablePagination from "@/components/ui/data-table-pagination";
import ResetUserPasswordDialog from "@/components/users/ResetUserPasswordDialog";
import UserProfile from "@/components/users/UserProfile";
import {
  useAdminUsers,
  useDeleteAdminUser,
  useActivateAdminUser,
  useDeactivateAdminUser,
} from "@/api-services/hooks/useAdminUsers";
import { useRolesDropdown } from "@/api-services/hooks/useRoles";
import { UserDTO } from "@/api-services/hooks/adminTypes";
import { PagedData } from "@/api-services/hooks/types";

const UsersPage: React.FC = () => {
  const can = usePermissionCheck();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize state from URL params
  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchParams.get("search") || "");
  const [statusFilter, setStatusFilter] = useState<string>(searchParams.get("status") || "all");
  const [roleFilter, setRoleFilter] = useState<string>(searchParams.get("role") || "all");

  // Pagination state (0-indexed for API)
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get("page") || "0", 10));
  const [itemsPerPage, setItemsPerPage] = useState(parseInt(searchParams.get("pageSize") || "10", 10));

  // Password reset dialog state
  const [passwordResetDialogOpen, setPasswordResetDialogOpen] = useState(false);
  const [selectedUserForPasswordReset, setSelectedUserForPasswordReset] = useState<User | null>(null);

  // Optimistic updates state - track status changes to avoid refetching
  const [optimisticStatusUpdates, setOptimisticStatusUpdates] = useState<Map<string, 'active' | 'inactive'>>(new Map());

  const { confirm, ConfirmDialog } = useConfirmDialog();

  // Fetch roles dropdown list from API
  const { data: rolesData, loading: rolesLoading } = useRolesDropdown({
    onError: (error) => {
      console.error('Failed to fetch roles:', error);
    },
  });
  // Handle both string array and object array formats
  const rolesDropdown = Array.isArray(rolesData)
    ? rolesData.map(r => typeof r === 'string' ? { roleName: r, roleId: r } : r)
    : [];

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => {
      clearTimeout(timer);
    };
  }, [searchTerm]);

  // Update URL params when filters change
  useEffect(() => {
    const params: Record<string, string> = {};
    if (searchTerm) params.search = searchTerm;
    if (statusFilter !== "all") params.status = statusFilter;
    if (roleFilter !== "all") params.role = roleFilter;
    if (currentPage > 0) params.page = String(currentPage);
    if (itemsPerPage !== 10) params.pageSize = String(itemsPerPage);
    setSearchParams(params);
  }, [searchTerm, statusFilter, roleFilter, currentPage, itemsPerPage, setSearchParams]);

  // Reset to first page when search or filters change
  useEffect(() => {
    setCurrentPage(0);
  }, [debouncedSearchTerm, statusFilter, roleFilter]);

  // Clean search term - if it looks like a phone number, strip special characters
  const cleanSearchTerm = (term: string): string => {
    const trimmed = term.trim();
    // If search term contains digits and phone-related characters, strip formatting
    if (/[\d+\-\s().]/.test(trimmed)) {
      // Remove all non-digit characters except the leading +
      return trimmed.replace(/[^\d+]/g, '');
    }
    return trimmed;
  };

  const cleanedSearchTerm = cleanSearchTerm(debouncedSearchTerm);

  // Reset to first page when search or filters change
  useEffect(() => {
    setCurrentPage(0);
  }, [debouncedSearchTerm, statusFilter, roleFilter]);

  // Only search if term is more than 2 characters (3+)
  const shouldSearch = debouncedSearchTerm.trim().length >= 2;

  // Determine if we need client-side filtering
  const hasClientSideFilters = statusFilter !== "all" || roleFilter !== "all";

  // When client-side filters are active, fetch a large page size to have enough data to filter
  const apiPageSize = hasClientSideFilters ? 1000 : itemsPerPage;
  const apiPage = hasClientSideFilters ? 0 : currentPage;

  // Fetch admin users from API (role and status filters are client-side)
  const usersQuery = useAdminUsers(
    {
      page: apiPage,
      size: apiPageSize,
      ...(shouldSearch && { search: cleanedSearchTerm }),
    },
    {
      onError: (error) => {
        toast.error(`Failed to load users: ${error.message}`);
      },
    }
  );

  // Convert UserDTO to User (UI type)
  const convertUserDTOToUser = useCallback((userDTO: UserDTO): User => {
    const fullName = userDTO.firstName && userDTO.lastName
      ? `${userDTO.firstName} ${userDTO.lastName}`
      : userDTO.userName || userDTO.email || 'Unknown User';

    // Extract roles - try multiple fields as backend might use different ones
    let userRoles: string[] = [];
    if (userDTO.roles && userDTO.roles.length > 0) {
      userRoles = userDTO.roles;
    } else if (userDTO.rolesAssigned) {
      // rolesAssigned might be a comma-separated string or JSON string
      try {
        if (userDTO.rolesAssigned.startsWith('[')) {
          userRoles = JSON.parse(userDTO.rolesAssigned);
        } else {
          userRoles = userDTO.rolesAssigned.split(',').map(r => r.trim());
        }
      } catch {
        userRoles = [userDTO.rolesAssigned];
      }
    } else if (userDTO.permissionsRoles && userDTO.permissionsRoles.length > 0) {
      userRoles = userDTO.permissionsRoles;
    }

    // Map roles to status
    const primaryRole = userRoles[0] || '';
    const roleMap: Record<string, string> = {
      'ROLE_SUPER_ADMIN': 'super_admin',
      'ROLE_ADMIN': 'admin',
      'ROLE_DRIVER': 'driver',
      'ROLE_PASSENGER': 'passenger',
      'ROLE_USER': 'user',
    };
    const mappedRole = roleMap[primaryRole] || primaryRole.toLowerCase().replace('role_', '');

    // Determine status from activated field
    let status: 'active' | 'inactive' | 'suspended' | 'pending' = 'pending';
    if (userDTO.activated === true) {
      status = 'active';
    } else if (userDTO.activated === false) {
      status = 'inactive';
    } else if (userDTO.status) {
      status = userDTO.status.toLowerCase() as any;
    }

    // Get user ID - try companyUserId first, then id
    const userId = userDTO.companyUserId || userDTO.id;

    // Filter out users with no valid ID or ID 0
    if (!userId || userId === 0) {
      return null;
    }

    return {
      id: String(userId),
      companyUserId: userDTO.companyUserId,
      name: fullName,
      userName: userDTO.userName,
      firstName: userDTO.firstName,
      lastName: userDTO.lastName,
      email: userDTO.email || '',
      phone: userDTO.mobile,
      phoneNumber: userDTO.mobile,
      phoneCountryCode: userDTO.phoneCountryCode,
      role: mappedRole,
      roleId: String(userId),
      roles: userRoles,
      rolesAssigned: userDTO.rolesAssigned,
      status: status,
      activated: userDTO.activated,
      emailVerified: true, // Assume verified if email exists
      phoneVerified: !!userDTO.mobile,
      lastLoginAt: userDTO.lastLoginDate,
      createdAt: userDTO.createdDate || userDTO.registeredDate || userDTO.createdAt || '',
      updatedAt: new Date().toISOString(),
      type: userDTO.type as any
    };
  }, []);

  // Convert API users to UI users
  const users: User[] = useMemo(() => {
    const userData = usersQuery.data as PagedData<UserDTO> | undefined;
    if (!userData?.content) return [];
    const convertedUsers = userData.content.map(convertUserDTOToUser);

    // Filter out null results (users with ID 0) and apply optimistic status updates
    let filteredUsers = convertedUsers.filter((user): user is User => user !== null);

    // Apply role filter (client-side)
    if (roleFilter !== "all") {
      filteredUsers = filteredUsers.filter((user) => {
        const userRoles = user.rolesAssigned || '';
        // Check if the user's roles include the selected role
        return userRoles.toLowerCase() === roleFilter.toLowerCase();
      });
    }

    // Apply status filter (client-side)
    if (statusFilter !== "all") {
      filteredUsers = filteredUsers.filter((user) => {
        if (statusFilter === "active") {
          return user.status === "active";
        } else if (statusFilter === "inactive") {
          return user.status === "inactive";
        }
        return true;
      });
    }

    // Apply optimistic status updates
    return filteredUsers.map((user) => {
      const optimisticStatus = optimisticStatusUpdates.get(user.id);
      if (optimisticStatus) {
        return {
          ...user,
          status: optimisticStatus,
          activated: optimisticStatus === 'active',
        };
      }
      return user;
    });
  }, [usersQuery.data, convertUserDTOToUser, optimisticStatusUpdates, roleFilter, statusFilter]);

  // Pagination calculations - use filtered count for client-side filters
  const userData = usersQuery.data as any;
  const totalFilteredItems = users.length;
  const totalItems = hasClientSideFilters ? totalFilteredItems : (userData?.page?.totalElements ?? 0);

  // Calculate total pages based on filtered items
  const totalPages = useMemo(() => {
    if (hasClientSideFilters) {
      // Client-side filtering: paginate filtered results
      return totalFilteredItems > 0 ? Math.ceil(totalFilteredItems / itemsPerPage) : 0;
    }
    // Server-side pagination: use API response
    return userData?.page?.totalPages ?? 0;
  }, [totalFilteredItems, itemsPerPage, hasClientSideFilters, userData]);

  // Paginated users for current page (when using client-side filtering)
  const paginatedUsers = useMemo(() => {
    if (!hasClientSideFilters) {
      // No client-side filtering, use API pagination directly
      return users;
    }
    // Client-side pagination for filtered results
    const startIndex = currentPage * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return users.slice(startIndex, endIndex);
  }, [users, currentPage, itemsPerPage, hasClientSideFilters]);

  const handlePageChange = (page: number) => {
    // Convert from 1-indexed (UI) to 0-indexed (API)
    setCurrentPage(page - 1);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(0); // Reset to first page when items per page changes
  };

  const getRoleName = (roles: string[]) => {
    if (!roles || roles.length === 0) return "User";

    // Direct role name from API (e.g., "Manager", "Supervisor")
    const primaryRole = roles[0];

    // Map for legacy ROLE_* format
    const roleMap: Record<string, string> = {
      'ROLE_SUPER_ADMIN': 'Super Admin',
      'ROLE_ADMIN': 'Admin',
      'ROLE_DRIVER': 'Driver',
      'ROLE_PASSENGER': 'Passenger',
      'ROLE_USER': 'User',
    };

    // Return mapped name if exists in roleMap, otherwise return the role name directly
    return roleMap[primaryRole] || primaryRole.replace('ROLE_', '').replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || primaryRole || 'User';
  };

  const getRoleIcon = (roles: string[]) => {
    if (!roles || roles.length === 0) return <UserCheck className="h-4 w-4" />;
    const primaryRole = roles[0];
    switch (primaryRole) {
      case "ROLE_SUPER_ADMIN":
      case "Super Admin":
      case "super_admin":
        return <Shield className="h-4 w-4" />;
      case "ROLE_ADMIN":
      case "Admin":
      case "admin":
        return <Settings className="h-4 w-4" />;
      case "ROLE_DRIVER":
      case "Driver":
      case "driver":
        return <Car className="h-4 w-4" />;
      case "ROLE_PASSENGER":
      case "Passenger":
      case "passenger":
        return <Users className="h-4 w-4" />;
      case "ROLE_USER":
      case "User":
      case "user":
        return <UserCheck className="h-4 w-4" />;
      default:
        // For custom roles, show a generic role icon
        return <Shield className="h-4 w-4" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "default";
      case "inactive":
        return "secondary";
      case "suspended":
        return "destructive";
      case "pending":
        return "outline";
      default:
        return "default";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "active":
        return <CheckCircle className="h-4 w-4" />;
      case "inactive":
        return <EyeOff className="h-4 w-4" />;
      case "suspended":
        return <X className="h-4 w-4" />;
      case "pending":
        return <Clock className="h-4 w-4" />;
      default:
        return <CheckCircle className="h-4 w-4" />;
    }
  };

  // Delete user mutation
  const deleteUserMutation = useDeleteAdminUser({
    onSuccess: (data, variables) => {
      toast.success("User deleted successfully!");
      usersQuery.refetch();
    },
    onError: (error: any, variables) => {
      let errorMessage = "Failed to delete user";
      if (error?.response?.status === 400) {
        errorMessage = "Cannot delete user. Please check if user has active rides.";
      } else if (error?.response?.status === 403) {
        errorMessage = "You don't have permission to delete this user.";
      } else if (error?.response?.status === 404) {
        errorMessage = "User not found. It may have already been deleted.";
      } else if (error?.message) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
    },
  });

  // Activate user mutation
  const activateUserMutation = useActivateAdminUser({
    onSuccess: (data, userId) => {
      toast.success("User activated successfully!");
      // Don't remove optimistic update - keep the local state to maintain UI consistency
      // This prevents the user from disappearing if the server filters them out
    },
    onError: (error, userId) => {
      toast.error(`Failed to activate user: ${error.message}`);
      // Remove optimistic update on error - will show original state
      setOptimisticStatusUpdates((prev) => {
        const next = new Map(prev);
        next.delete(String(userId));
        return next;
      });
    },
  });

  // Deactivate user mutation
  const deactivateUserMutation = useDeactivateAdminUser({
    onSuccess: (data, userId) => {
      toast.success("User deactivated successfully!");
      // Don't remove optimistic update - keep the local state to maintain UI consistency
      // This prevents the user from disappearing if the server filters them out
    },
    onError: (error, userId) => {
      toast.error(`Failed to deactivate user: ${error.message}`);
      // Remove optimistic update on error - will show original state
      setOptimisticStatusUpdates((prev) => {
        const next = new Map(prev);
        next.delete(String(userId));
        return next;
      });
    },
  });

  const handleStatusToggle = (user: User) => {
    const isActive = user.status === "active";
    const userName = user.name || user.userName || user.email;
    const userId = parseInt(user.id, 10);

    if (isNaN(userId)) {
      toast.error("Invalid user ID");
      return;
    }

    confirm(
      createDeactivateConfirm("User", isActive, async () => {
        // Apply optimistic update immediately
        const newStatus = isActive ? 'inactive' : 'active';
        setOptimisticStatusUpdates((prev) => {
          const next = new Map(prev);
          next.set(user.id, newStatus);
          return next;
        });

        if (isActive) {
          await deactivateUserMutation.mutate(userId);
        } else {
          await activateUserMutation.mutate(userId);
        }
      })
    );
  };

  const handleResetPassword = (user: User) => {
    setSelectedUserForPasswordReset(user);
    setPasswordResetDialogOpen(true);
  };

  const handlePasswordResetSuccess = () => {
    // Refresh user list after password reset
    usersQuery.refetch();
  };

  const handleDelete = (user: User) => {
    const userName = user.name || user.userName || user.email;
    const userId = parseInt(user.id, 10);

    if (isNaN(userId)) {
      toast.error("Invalid user ID");
      return;
    }

    confirm(
      createDeleteConfirm(
        "User",
        async () => {
          await deleteUserMutation.mutate({
            userId,
            email: user.email,
          });
        },
        `Are you sure you want to delete ${userName}? This will permanently remove the user and all associated data, including corresponding driver and rider accounts. This action cannot be undone.`
      )
    );
  };

  const handleAddUser = () => {
    navigate("/admin/users/add");
  };

  const handleEditUser = (userId: string) => {
    console.log('Editing user with ID:', userId);
    if (!userId || userId === 'undefined' || userId === 'null') {
      toast.error('User ID is missing. Cannot edit user.');
      return;
    }

    // Find the user data to pass to edit page
    const user = users.find(u => u.id === userId);

    // Also pass the original UserDTO from API response to preserve metadata fields
    const apiData = usersQuery.data as PagedData<UserDTO> | undefined;
    const originalDTO = apiData?.content?.find(
      (u) => String(u.id) === userId || String(u.companyUserId) === userId
    );

    navigate(`/admin/users/edit/${userId}`, {
      state: { user, userDTO: originalDTO }
    });
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "Never";
    return new Date(dateString).toLocaleDateString();
  };

  const getInitials = (name?: string) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Users</h1>
          <p className="text-muted-foreground">
            Manage user accounts, roles, and permissions
          </p>
        </div>
        {can('Create Users') && (
          <Button onClick={handleAddUser}>
            <Plus className="w-4 h-4 mr-2" />
            Add User
          </Button>
        )}
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search Input */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Search users by name, email, or phone... (min 3 characters)"
                className="pl-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* Status Filter Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="w-40">
                  <Filter className="w-4 h-4 mr-2" />
                  {statusFilter === "all"
                    ? "All Status"
                    : statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setStatusFilter("all")}>
                  All Status
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setStatusFilter("active")}>
                  Active
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setStatusFilter("inactive")}>
                  Inactive
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Role Filter Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="w-40" disabled={rolesLoading}>
                  <Shield className="w-4 h-4 mr-2" />
                  {roleFilter === "all"
                    ? "All Roles"
                    : rolesDropdown.find(r => r.roleName === roleFilter)?.roleName || roleFilter}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setRoleFilter("all")}>
                  All Roles
                </DropdownMenuItem>
                {rolesDropdown.map((role) => (
                  <DropdownMenuItem key={role.roleId} onClick={() => setRoleFilter(role.roleName)}>
                    {role.roleName}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardHeader>
          <CardTitle>
            Users (
            {usersQuery.loading 
              ? "..." 
              : statusFilter !== "all" || roleFilter !== "all"
                ? `${totalItems} filtered`
                : totalItems > 0 ? totalItems : (userData?.content?.length ?? 0)}
            )
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Username</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Last Login</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {usersQuery.loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    <div className="flex flex-col items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-2"></div>
                      <p className="text-muted-foreground">Loading users...</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : paginatedUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-medium">No users found</h3>
                    <p className="text-muted-foreground">
                      {searchTerm
                        ? "Try adjusting your filters"
                        : "Get started by adding your first user"}
                    </p>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedUsers.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="h-8 w-8">
                          <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                            {getInitials(user.name)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <div className="font-medium">{user.name}</div>
                          <div className="text-sm text-muted-foreground">
                            ID: {user.id.slice(0, 8)}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm font-mono">{user.userName || '-'}</div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 text-sm">
                          <Mail className="h-4 w-4 text-muted-foreground" />
                          <span className="text-muted-foreground truncate max-w-[200px]">
                            {user.email}
                          </span>
                          {user.emailVerified && (
                            <CheckCircle className="h-4 w-4 text-green-500" />
                          )}
                        </div>
                        {user.phone && (
                          <div className="flex items-center gap-2 text-sm">
                            <span className="text-muted-foreground">
                              {user.phoneCountryCode} {user.phone}
                            </span>
                            {user.phoneVerified && (
                              <CheckCircle className="h-4 w-4 text-green-500" />
                            )}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Shield className="h-4 w-4 text-muted-foreground" />
                        <span>{user.rolesAssigned || 'No role assigned'}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={getStatusColor(user.status)}
                        className="flex items-center gap-1 capitalize"
                      >
                        {getStatusIcon(user.status)}
                        <span>{user.status || "unknown"}</span>
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-muted-foreground">
                        {formatDate(user.lastLoginAt)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-muted-foreground">
                        {formatDate(user.createdAt)}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" className="h-8 w-8 p-0">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {can('Edit Users') && (
                            <DropdownMenuItem onClick={() => handleEditUser(user.id)}>
                              <Edit className="mr-2 h-4 w-4" />
                              Edit User
                            </DropdownMenuItem>
                          )}
                          {can('Reset User Password') && (
                            <DropdownMenuItem onClick={() => handleResetPassword(user)}>
                              <Key className="mr-2 h-4 w-4" />
                              Reset Password
                            </DropdownMenuItem>
                          )}
                          {can('Activate/Deactivate Users') && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => handleStatusToggle(user)}
                                disabled={activateUserMutation.loading || deactivateUserMutation.loading}
                              >
                                {user.status === "active" ? (
                                  <>
                                    <EyeOff className="mr-2 h-4 w-4" />
                                    Deactivate
                                  </>
                                ) : (
                                  <>
                                    <CheckCircle className="mr-2 h-4 w-4" />
                                    Activate
                                  </>
                                )}
                              </DropdownMenuItem>
                            </>
                          )}
                          {can('Delete Users') && (
                            <DropdownMenuItem
                              onClick={() => handleDelete(user)}
                              className="text-red-600"
                              disabled={deleteUserMutation.loading}
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Delete User
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {/* Pagination */}
          {!usersQuery.loading && paginatedUsers.length > 0 && totalPages > 0 && (
            <DataTablePagination
              currentPage={currentPage + 1} // Convert from 0-indexed to 1-indexed for UI
              totalPages={totalPages}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
              className="mt-4"
            />
          )}
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <ConfirmDialog />

      {/* Reset Password Dialog */}
      {selectedUserForPasswordReset && (
        <ResetUserPasswordDialog
          open={passwordResetDialogOpen}
          onOpenChange={setPasswordResetDialogOpen}
          userId={selectedUserForPasswordReset.id}
          userName={selectedUserForPasswordReset.name || selectedUserForPasswordReset.userName || ""}
          userEmail={selectedUserForPasswordReset.email}
          onSuccess={handlePasswordResetSuccess}
        />
      )}

    </div>
  );
};

export default UsersPage;
